/**
 * Division mapper — mirror of wcc4-backend
 *   src/modules/game-config/game-config.data.ts (entries 1..21).
 *
 * Used throughout analytics dashboards so the raw `divisions` integer
 * (1..21) renders as the player-facing tier name. New tiers should be
 * added to both the backend seed and this map in lock-step.
 */

/**
 * Authoritative tier-name mapping (rookie → amateur → pro → star →
 * elite → master → legendary, each as III / II / I). Mirror in
 * wcc4-backend admin DIVISION_LABELS and any client/Unity-side enum
 * — they must all agree.
 */
export const DIVISION_NAMES: Record<number, string> = {
  1: 'Rookie League III',
  2: 'Rookie League II',
  3: 'Rookie League I',
  4: 'Semi-Pro III',
  5: 'Semi-Pro II',
  6: 'Semi-Pro I',
  7: 'Pro III',
  8: 'Pro II',
  9: 'Pro I',
  10: 'National III',
  11: 'National II',
  12: 'National I',
  13: 'Continental III',
  14: 'Continental II',
  15: 'Continental I',
  16: 'Global III',
  17: 'Global II',
  18: 'Global I',
  19: 'Legendary III',
  20: 'Legendary II',
  21: 'Legendary I',
};

/**
 * Short label for compact UI surfaces (e.g. table cell, axis label).
 * Falls back to "Div N" when the integer is outside the 1..21 range
 * so unmapped data still renders rather than collapsing to empty.
 */
export const divisionName = (n: number | null | undefined): string => {
  if (n == null) return '-';
  return DIVISION_NAMES[n] ?? `Div ${n}`;
};

/**
 * League family grouping — useful for higher-level breakdowns
 * (rookie / semi-pro / pro / national / continental / global / legendary).
 * Index 0 is unused so the slot matches the tier ordering 1..21.
 */
export const DIVISION_LEAGUE: Record<number, string> = {
  1: 'Rookie League',
  2: 'Rookie League',
  3: 'Rookie League',
  4: 'Semi-Pro',
  5: 'Semi-Pro',
  6: 'Semi-Pro',
  7: 'Pro',
  8: 'Pro',
  9: 'Pro',
  10: 'National',
  11: 'National',
  12: 'National',
  13: 'Continental',
  14: 'Continental',
  15: 'Continental',
  16: 'Global',
  17: 'Global',
  18: 'Global',
  19: 'Legendary',
  20: 'Legendary',
  21: 'Legendary',
};

export const divisionLeague = (n: number | null | undefined): string => {
  if (n == null) return '-';
  return DIVISION_LEAGUE[n] ?? 'Unknown';
};
